// ─── Impostazioni → Regole per i soci → «Premio e Crediti» ──────────
//
// 30/09/2026, mockup «Premio, Crediti e campi fissi in una sezione», approvato:
// *«1A 2A»*. 🗣️ *«mi ottimizzi le voci campi periodici + premio + crediti
// perché sono tutti e tre complementari»*. Tre riquadri, nell'ordine della
// storia: ① chi gioca con regolarità si prende un campo fisso, ② chi organizza
// spesso riceve il premio, ③ il premio arriva in Crediti, che hanno un valore e
// una scadenza. Erano tre linguette che si rimandavano l'una all'altra.
//
// ⚖️ I valori restano DOVE SONO: il campo fisso nelle impostazioni di prima
// (`/settings/partite`, rimandate indietro intatte, tranne `fissoDalBot`), il
// premio in `/settings/premio`, i Crediti in `/settings/crediti`. Un solo
// «Salva» li scrive tutti e tre, uno dopo l'altro.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  VALORE_CREDITO_CENTS,
  type ImpostazioniPartite,
  type ImpostazioniCrediti,
  type ImpostazioniPremio,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, euro, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'premio-e-crediti';

type Salvate = { fisso: ImpostazioniPartite; premio: ImpostazioniPremio; crediti: ImpostazioniCrediti };

type Modulo = {
  fissoAttivo: boolean;
  fissoMesi: string;
  premioAttivo: boolean;
  premioDal: string;
  premioCrediti: string;
  mesi: string;
  avviso: string;
};

const daSalvate = (s: Salvate): Modulo => ({
  fissoAttivo: s.fisso.fissoDalBot.attivo,
  fissoMesi: String(s.fisso.fissoDalBot.mesiMax),
  premioAttivo: s.premio.attivo,
  premioDal: s.premio.dal ?? '',
  premioCrediti: String(s.premio.creditiPerPartita),
  mesi: String(s.crediti.mesi),
  avviso: String(s.crediti.giorniAvviso),
});

const intero = (s: string, min: number, max: number): number | undefined => {
  const n = Number(s.trim());
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

export function PremioECrediti(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: async (): Promise<Salvate> => {
      const [fisso, premio, crediti] = await Promise.all([
        chiama<ImpostazioniPartite>('GET', '/settings/partite'),
        chiama<ImpostazioniPremio>('GET', '/settings/premio'),
        chiama<ImpostazioniCrediti>('GET', '/settings/crediti'),
      ]);
      return { fisso, premio, crediti };
    },
  });
  if (letta.isLoading) return <Attesa cosa="il premio, i Crediti e i campi fissi" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return <Pagina salvate={letta.data} modificabile={modificabile} />;
}

function Pagina({ salvate, modificabile }: { salvate: Salvate; modificabile: boolean }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [m, setM] = useState<Modulo>(() => daSalvate(salvate));
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setM(daSalvate(salvate)), [salvate]);
  const cambia = (f: (x: Modulo) => Modulo) => {
    setSalvato(false);
    setM(f);
  };

  const fissoMesi = intero(m.fissoMesi, 1, 6);
  const premioCrediti = intero(m.premioCrediti, 1, 20);
  const mesi = intero(m.mesi, 1, 60);
  const avviso = intero(m.avviso, 1, 120);
  const pronto =
    fissoMesi !== undefined && premioCrediti !== undefined && mesi !== undefined && avviso !== undefined;

  const salva = useMutation({
    meta: { fatto: 'premio, Crediti e campi fissi salvati' },
    mutationFn: async () => {
      await chiama<ImpostazioniPartite>('PUT', '/settings/partite', {
        // Gli avvisi come sono salvati: di queste impostazioni qui si tocca solo il campo fisso.
        avvisi: salvate.fisso.avvisi,
        fissoDalBot: { attivo: m.fissoAttivo, mesiMax: fissoMesi },
      });
      await chiama<ImpostazioniPremio>('PUT', '/settings/premio', {
        attivo: m.premioAttivo,
        dal: m.premioDal || null,
        creditiPerPartita: premioCrediti,
      });
      await chiama<ImpostazioniCrediti>('PUT', '/settings/crediti', { mesi, giorniAvviso: avviso });
    },
    onSuccess: () => setSalvato(true),
    // Anche a metà strada: la pagina rilegge quello che è stato salvato davvero.
    onSettled: () => rileggi(),
  });

  return (
    <section>
      <header className="testata">
        <h3>Premio e Crediti</h3>
        <SoloLettura mostra={modificabile} />
      </header>

      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto && modificabile) salva.mutate();
        }}
      >
        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>① 🔁 Campi fissi dal bot</h3>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={m.fissoAttivo}
              onChange={(e) => cambia((x) => ({ ...x, fissoAttivo: e.target.checked }))}
            />{' '}
            I soci possono prenotare un campo fisso dall’assistente
          </label>
          <Campo
            etichetta="Durata massima, in mesi"
            inputMode="numeric"
            value={m.fissoMesi}
            onChange={(e) => cambia((x) => ({ ...x, fissoMesi: e.target.value }))}
          />
          {fissoMesi === undefined && <p className="guasto">Scrivi un numero da 1 a 6, per esempio 2.</p>}
          <p className="tenue piccolo">
            Ogni settimana, stesso giorno, stessa ora, stesso campo: gli altri ritmi e le durate più lunghe li
            fa la Segreteria. Il campo fisso nasce subito, come una prenotazione, e nel calendario ha il segno
            🔁 «dal bot».
          </p>
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>② 🎁 Premio a chi organizza</h3>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={m.premioAttivo}
              onChange={(e) => cambia((x) => ({ ...x, premioAttivo: e.target.checked }))}
            />{' '}
            <strong>{m.premioAttivo ? 'Acceso' : 'Spento'}</strong>
          </label>
          <p className="tenue piccolo">
            Chi organizza una partita e ne ha un’altra, sua, entro 7 giorni (anche la stessa sera) riceve dei
            Crediti per ciascuna. Si vedono subito «in arrivo» e diventano spendibili a fine partita. Spento,
            non nascono Crediti nuovi; quelli già dati restano spendibili. Prima di accenderlo, fai confermare
            al commercialista come va scritto lo sconto sullo scontrino.
          </p>
          <label className="campo">
            <span>Contano le partite giocate dal (vuoto: da oggi)</span>
            <input
              type="date"
              value={m.premioDal}
              onChange={(e) => cambia((x) => ({ ...x, premioDal: e.target.value }))}
            />
          </label>
          <Campo
            etichetta="Crediti per ogni partita"
            inputMode="numeric"
            value={m.premioCrediti}
            onChange={(e) => cambia((x) => ({ ...x, premioCrediti: e.target.value }))}
          />
          {premioCrediti === undefined && <p className="guasto">Fra 1 e 20 Crediti.</p>}
        </fieldset>

        <fieldset className="riquadro" disabled={!modificabile}>
          <h3>③ 🎟️ Crediti</h3>
          <p className="tenue piccolo">
            1 Credito = {euro(VALORE_CREDITO_CENTS)} di sconto sulla quota di una partita. Non si prelevano,
            non si rimborsano, non si passano ad altri. Arrivano dal premio qui sopra.
          </p>
          <Campo
            etichetta="I Crediti valgono (mesi)"
            inputMode="numeric"
            value={m.mesi}
            onChange={(e) => cambia((x) => ({ ...x, mesi: e.target.value }))}
          />
          {mesi === undefined && <p className="guasto">Fra 1 e 60 mesi.</p>}
          <Campo
            etichetta="Avviso dei Crediti in scadenza (giorni prima)"
            inputMode="numeric"
            value={m.avviso}
            onChange={(e) => cambia((x) => ({ ...x, avviso: e.target.value }))}
          />
          {avviso === undefined && <p className="guasto">Fra 1 e 120 giorni.</p>}
          <p className="tenue piccolo">
            Vale per tutti i Crediti. Cambiandola, i Crediti che nascono da quel momento prendono la regola
            nuova; quelli già dati tengono la loro data.
          </p>
        </fieldset>

        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && <p className="tenue">Salvato.</p>}
            <button type="submit" disabled={salva.isPending || !pronto}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
    </section>
  );
}
